<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <section class="tech-hero w-full rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <p class="tech-hero__eyebrow">IT Inventory · {{ dashboardYearLabel }}</p>
                            <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                                {{ campusTitle }} Technology
                            </h1>
                            <p class="mt-2 text-gray-600 max-w-2xl leading-relaxed">
                                Live campus IT inventory overview — devices, utilization, and infrastructure at a glance.
                                Use the year filter to refresh KPIs and charts.
                            </p>
                        </div>
                        <div class="tech-hero__meta shrink-0">
                            <div class="tech-hero__badge">
                                <span class="tech-hero__badge-label">Entity</span>
                                <span class="tech-hero__badge-value">{{ entity }} · {{ campusCode }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="tech-year-bar">
                        <div class="tech-year-bar__label">
                            <font-awesome-icon :icon="['fas', 'chart-bar']" class="tech-year-bar__icon" />
                            <span>Dashboard statistics</span>
                        </div>
                        <div class="tech-year-bar__pills">
                            <button
                                v-for="opt in yearFilterOptions"
                                :key="'dash-' + opt.value + opt.label"
                                type="button"
                                class="tech-year-pill"
                                :class="{ 'tech-year-pill--active': dashboardYear === opt.value }"
                                @click="setDashboardYear(opt.value)"
                            >
                                {{ opt.label }}
                            </button>
                        </div>
                    </div>
                </section>

                <div v-if="pageLoading" class="flex justify-center py-10">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                </div>

                <template v-else>
                    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div class="tech-kpi">
                            <div class="tech-kpi__label">Total assets</div>
                            <div class="tech-kpi__value">{{ inventoryTotals.totalAssets }}</div>
                            <div class="tech-kpi__hint">{{ dashboardYearLabel }}</div>
                        </div>
                        <div class="tech-kpi">
                            <div class="tech-kpi__label">Endpoints</div>
                            <div class="tech-kpi__value">{{ inventoryTotals.endpoints }}</div>
                            <div class="tech-kpi__hint">Desktops · Screens · Laptops</div>
                        </div>
                        <div class="tech-kpi tech-kpi--ok">
                            <div class="tech-kpi__label">In use</div>
                            <div class="tech-kpi__value">{{ inventoryTotals.inUse }}</div>
                            <div class="tech-kpi__hint">Tracked device status</div>
                        </div>
                        <div class="tech-kpi tech-kpi--stock">
                            <div class="tech-kpi__label">In stock</div>
                            <div class="tech-kpi__value">{{ inventoryTotals.inStock }}</div>
                            <div class="tech-kpi__hint">Available inventory</div>
                        </div>
                    </div>

                    <section class="mb-8">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                                <font-awesome-icon :icon="['fas', 'chart-pie']" class="text-teal-600 mr-2" />
                                Analytics
                            </h2>
                            <span class="text-xs text-gray-500">{{ dashboardYearLabel }}</span>
                        </div>

                        <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
                            <InventoryOverviewChart
                                class="xl:col-span-2"
                                :categories="inventoryOverview.categories"
                                :values="inventoryOverview.values"
                                :module-keys="inventoryOverview.moduleKeys"
                                @select="onInventoryCategorySelect"
                            />
                            <UtilizationStatusChart
                                :categories="utilizationChart.categories"
                                :in-use="utilizationChart.inUse"
                                :in-stock="utilizationChart.inStock"
                            />
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            <TechDonutChart
                                title="Desktops &amp; Screens"
                                eyebrow="Composition"
                                :labels="desktopsComposition.labels"
                                :values="desktopsComposition.values"
                                :colors="['#0f766e', '#0891b2']"
                                :total="desktopsComposition.total"
                            />
                            <TechDonutChart
                                title="Display devices"
                                eyebrow="Classrooms"
                                :labels="displayDevices.labels"
                                :values="displayDevices.values"
                                :colors="['#0d9488', '#0369a1']"
                                :total="displayDevices.total"
                            />
                            <TechDonutChart
                                title="Infrastructure"
                                eyebrow="Campus IT"
                                :labels="infrastructureMix.labels"
                                :values="infrastructureMix.values"
                                :module-keys="infrastructureMix.moduleKeys"
                                :colors="['#0f766e', '#0d9488', '#0891b2', '#0369a1', '#334155', '#64748b']"
                                :total="infrastructureMix.total"
                                @select="onInventoryCategorySelect"
                            />
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                            <TechDonutChart
                                title="By brand"
                                eyebrow="Laptops"
                                :labels="laptopsByBrand.labels"
                                :values="laptopsByBrand.values"
                                :module-keys="laptopsByBrand.moduleKeys"
                                :colors="['#0f766e', '#0891b2', '#0369a1', '#64748b']"
                                :total="laptopsByBrand.total"
                                @select="onInventoryCategorySelect"
                            />
                            <TechDonutChart
                                title="By brand"
                                eyebrow="Printers"
                                :labels="printersByBrand.labels"
                                :values="printersByBrand.values"
                                :module-keys="printersByBrand.moduleKeys"
                                :colors="['#0d9488', '#0f766e', '#0369a1', '#64748b']"
                                :total="printersByBrand.total"
                                @select="onInventoryCategorySelect"
                            />
                            <TechDonutChart
                                title="By floor"
                                eyebrow="CCTV"
                                :labels="cctvByFloor.labels"
                                :values="cctvByFloor.values"
                                :module-keys="cctvByFloor.moduleKeys"
                                :colors="['#0f766e', '#0d9488', '#0891b2', '#0369a1', '#334155']"
                                :total="cctvByFloor.total"
                                @select="onInventoryCategorySelect"
                            />
                            <TechDonutChart
                                title="By device"
                                eyebrow="Scrap"
                                :labels="scrapByDevice.labels"
                                :values="scrapByDevice.values"
                                :module-keys="scrapByDevice.moduleKeys"
                                :colors="['#0f766e', '#0d9488', '#0891b2', '#0369a1', '#334155', '#64748b']"
                                :total="scrapByDevice.total"
                                @select="onInventoryCategorySelect"
                            />
                        </div>
                    </section>

                    <section class="bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                        <div class="flex items-center justify-between mb-6">
                            <h2 class="text-lg font-semibold text-gray-800 flex items-center">
                                <font-awesome-icon :icon="['fas', 'th-large']" class="text-teal-600 mr-2" />
                                Inventory Sections
                            </h2>
                            <span class="text-xs text-gray-500">
                                {{ inventoryTotals.totalAssets }} assets · {{ dashboardYearLabel }}
                            </span>
                        </div>

                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            <button
                                v-for="module in modules"
                                :key="module.key"
                                type="button"
                                class="tech-module"
                                :class="{
                                    'tech-module--active': module.enabled,
                                    'tech-module--soon': !module.enabled,
                                }"
                                :disabled="!module.enabled"
                                @click="openModule(module)"
                            >
                                <div class="tech-module__icon">{{ module.icon }}</div>
                                <div class="tech-module__label">{{ module.label }}</div>
                                <div v-if="module.enabled && module.count !== null" class="tech-module__count">
                                    {{ module.count }} {{ pluralize(module.unit, module.count) }}
                                </div>
                                <div v-else-if="!module.enabled" class="tech-module__soon">Coming soon</div>
                            </button>
                        </div>
                    </section>
                </template>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal
                v-if="activeModule"
                :title="activeModule.label"
                @close="closeModule"
            >
                <component
                    :is="activeModuleComponent"
                    :key="activeModule.key"
                    :entity="entity"
                    :campus="campusCode"
                    :year-filter-options="yearFilterOptions"
                    :form-year-options="formYearOptions"
                    :default-year="dashboardYear"
                    @changed="onModuleChanged"
                />
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import gql from "graphql-tag";
import BreadCrumb from "../../../components/shared/BreadCrumb.vue";
import InventoryOverviewChart from "./charts/InventoryOverviewChart.vue";
import TechDonutChart from "./charts/TechDonutChart.vue";
import UtilizationStatusChart from "./charts/UtilizationStatusChart.vue";
import {
    ADPOLY_ENTITY,
    getCampusConfig,
    isValidCampus,
    getTechnologyYear,
    buildTechnologyYearLists,
    TECHNOLOGY_YEAR_FILTER_ALL,
} from "../config";

const YEARS_QUERY = gql`
    query TechnologyYears($entity: String, $campus: String) {
        itDesktopsAndScreensYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itLaptopsYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itWhiteboardsAndProjectorsYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itPrintersYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itSoftwareYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itIpPhonesYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itCctvCamerasYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itIdfRoomsYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itLmsSystemsYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
        itScrapItemsYears(entity: $entity, campus: $campus) {
            error
            error_code
            message
            data
        }
    }
`;

const SUMMARY_QUERY = gql`
    query TechnologySummary($entity: String, $campus: String, $year: String) {
        itDesktopsAndScreensSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                desktop_count
                screen_count
                in_use_count
                in_stock_count
                desktop_in_use_count
                desktop_in_stock_count
                screen_in_use_count
                screen_in_stock_count
            }
        }
        itLaptopsSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                in_use_count
                in_stock_count
                by_brand {
                    label
                    value
                }
            }
        }
        itWhiteboardsAndProjectorsSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                whiteboard_count
                projector_count
            }
        }
        itPrintersSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                in_use_count
                in_stock_count
                by_brand {
                    label
                    value
                }
            }
        }
        itSoftwareSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
            }
        }
        itIpPhonesSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
            }
        }
        itCctvCamerasSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                by_floor {
                    label
                    value
                }
            }
        }
        itIdfRoomsSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
            }
        }
        itLmsSystemsSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
            }
        }
        itScrapItemsSummary(entity: $entity, campus: $campus, year: $year) {
            error
            error_code
            message
            data {
                total
                by_type {
                    label
                    value
                }
            }
        }
    }
`;

var moduleComponentCache = {};

function resolveModuleComponent(key) {
    if (!key) {
        return null;
    }
    if (!moduleComponentCache[key]) {
        moduleComponentCache[key] = defineAsyncComponent(function () {
            return import("./modules/" + key + "Module.vue");
        });
    }
    return moduleComponentCache[key];
}

export default {
    name: "adpoly-technology",
    components: {
        BreadCrumb,
        InventoryOverviewChart,
        TechDonutChart,
        UtilizationStatusChart,
    },
    props: ["campus"],
    data() {
        return {
            entity: ADPOLY_ENTITY,
            currentYear: getTechnologyYear(),
            dashboardYear: getTechnologyYear(),
            yearsLoading: true,
            summaryLoading: false,
            yearFilterOptions: [{ value: TECHNOLOGY_YEAR_FILTER_ALL, label: "All" }],
            formYearOptions: [getTechnologyYear()],
            summary: {
                total: 0,
                desktop_count: 0,
                screen_count: 0,
                in_use_count: 0,
                in_stock_count: 0,
            },
            moduleSummaries: {
                desktops: {
                    total: 0,
                    desktop_count: 0,
                    screen_count: 0,
                    in_use_count: 0,
                    in_stock_count: 0,
                    desktop_in_use_count: 0,
                    desktop_in_stock_count: 0,
                    screen_in_use_count: 0,
                    screen_in_stock_count: 0,
                },
                laptops: {
                    total: 0,
                    in_use_count: 0,
                    in_stock_count: 0,
                    by_brand: [],
                },
                whiteboards: {
                    total: 0,
                    whiteboard_count: 0,
                    projector_count: 0,
                },
                printers: {
                    total: 0,
                    in_use_count: 0,
                    in_stock_count: 0,
                    by_brand: [],
                },
                software: { total: 0 },
                ipPhones: { total: 0 },
                cctv: { total: 0, by_floor: [] },
                idfRooms: { total: 0 },
                lms: { total: 0 },
                scrap: { total: 0, by_type: [] },
            },
            activeModule: null,
            modules: [
                {
                    key: "DesktopsAndScreens",
                    label: "Desktops & Screens",
                    icon: "🖥️",
                    unit: "device",
                    enabled: true,
                    count: null,
                },
                { key: "Laptops", label: "Laptops", icon: "💻", unit: "laptop", enabled: true, count: null },
                {
                    key: "WhiteboardsAndProjectors",
                    label: "Whiteboard & Projectors",
                    icon: "📽️",
                    unit: "device",
                    enabled: true,
                    count: null,
                },
                { key: "Printers", label: "Printers", icon: "🖨️", unit: "printer", enabled: true, count: null },
                { key: "Users", label: "Users", icon: "👥", unit: "user", enabled: false, count: null },
                { key: "Software", label: "Software", icon: "💾", unit: "software", enabled: true, count: null },
                { key: "IPPhones", label: "IP Phones", icon: "📞", unit: "phone", enabled: true, count: null },
                { key: "CCTV", label: "CCTV", icon: "📹", unit: "camera", enabled: true, count: null },
                { key: "IDFRooms", label: "IDF Rooms", icon: "🏬", unit: "room", enabled: true, count: null },
                { key: "ServerRoom", label: "Server Room", icon: "🖧", unit: "room", enabled: false, count: null },
                { key: "LMS", label: "LMS", icon: "📚", unit: "system", enabled: true, count: null },
                { key: "Scrap", label: "Scrap", icon: "🗑️", unit: "item", enabled: true, count: null },
            ],
        };
    },
    created() {
        this.validateCampus();
        this.loadYears();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.loadYears();
        },
    },
    computed: {
        pageLoading() {
            return this.yearsLoading || this.summaryLoading;
        },
        dashboardYearLabel() {
            if (this.dashboardYear === TECHNOLOGY_YEAR_FILTER_ALL) {
                return "All years";
            }
            return this.dashboardYear || this.currentYear;
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        breadCrumbItems() {
            var campusKey = this.campusConfig ? this.campusConfig.key : "";
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: campusKey ? "/adpoly/" + campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: campusKey ? "/adpoly/" + campusKey + "/campus" : "/adpoly",
                },
                { label: "Technology" },
            ];
        },
        activeModuleComponent() {
            if (!this.activeModule) {
                return null;
            }
            return resolveModuleComponent(this.activeModule.key);
        },
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        inventoryTotals() {
            var ms = this.moduleSummaries;
            var desktops = ms.desktops || {};
            var laptops = ms.laptops || {};
            var whiteboards = ms.whiteboards || {};
            var printers = ms.printers || {};
            var software = ms.software || {};
            var ipPhones = ms.ipPhones || {};
            var cctv = ms.cctv || {};
            var idfRooms = ms.idfRooms || {};
            var lms = ms.lms || {};
            var scrap = ms.scrap || {};

            var totalAssets =
                (desktops.total || 0) +
                (laptops.total || 0) +
                (whiteboards.total || 0) +
                (printers.total || 0) +
                (software.total || 0) +
                (ipPhones.total || 0) +
                (cctv.total || 0) +
                (idfRooms.total || 0) +
                (lms.total || 0) +
                (scrap.total || 0);

            return {
                totalAssets: totalAssets,
                endpoints:
                    (desktops.desktop_count || 0) +
                    (desktops.screen_count || 0) +
                    (laptops.total || 0),
                inUse:
                    (desktops.in_use_count || 0) +
                    (laptops.in_use_count || 0) +
                    (printers.in_use_count || 0),
                inStock:
                    (desktops.in_stock_count || 0) +
                    (laptops.in_stock_count || 0) +
                    (printers.in_stock_count || 0),
            };
        },
        inventoryOverview() {
            var ms = this.moduleSummaries;
            return {
                categories: [
                    "Desktops",
                    "Laptops",
                    "Displays",
                    "Printers",
                    "Software",
                    "IP Phones",
                    "CCTV",
                    "IDF",
                    "LMS",
                    "Scrap",
                ],
                moduleKeys: [
                    "DesktopsAndScreens",
                    "Laptops",
                    "WhiteboardsAndProjectors",
                    "Printers",
                    "Software",
                    "IPPhones",
                    "CCTV",
                    "IDFRooms",
                    "LMS",
                    "Scrap",
                ],
                values: [
                    (ms.desktops && ms.desktops.total) || 0,
                    (ms.laptops && ms.laptops.total) || 0,
                    (ms.whiteboards && ms.whiteboards.total) || 0,
                    (ms.printers && ms.printers.total) || 0,
                    (ms.software && ms.software.total) || 0,
                    (ms.ipPhones && ms.ipPhones.total) || 0,
                    (ms.cctv && ms.cctv.total) || 0,
                    (ms.idfRooms && ms.idfRooms.total) || 0,
                    (ms.lms && ms.lms.total) || 0,
                    (ms.scrap && ms.scrap.total) || 0,
                ],
            };
        },
        utilizationChart() {
            var ms = this.moduleSummaries;
            var ds = ms.desktops || {};
            var lp = ms.laptops || {};
            var pr = ms.printers || {};
            return {
                categories: ["Desktop", "Screen", "Laptop", "Printer"],
                inUse: [
                    ds.desktop_in_use_count || 0,
                    ds.screen_in_use_count || 0,
                    lp.in_use_count || 0,
                    pr.in_use_count || 0,
                ],
                inStock: [
                    ds.desktop_in_stock_count || 0,
                    ds.screen_in_stock_count || 0,
                    lp.in_stock_count || 0,
                    pr.in_stock_count || 0,
                ],
            };
        },
        desktopsComposition() {
            var ds = this.moduleSummaries.desktops || {};
            return {
                labels: ["Desktops", "Screens"],
                values: [ds.desktop_count || 0, ds.screen_count || 0],
                total: ds.total || 0,
            };
        },
        displayDevices() {
            var wp = this.moduleSummaries.whiteboards || {};
            return {
                labels: ["Whiteboards", "Projectors"],
                values: [wp.whiteboard_count || 0, wp.projector_count || 0],
                total: wp.total || 0,
            };
        },
        infrastructureMix() {
            var ms = this.moduleSummaries;
            return {
                labels: ["CCTV", "IDF Rooms", "LMS", "Software", "IP Phones", "Scrap"],
                moduleKeys: ["CCTV", "IDFRooms", "LMS", "Software", "IPPhones", "Scrap"],
                values: [
                    (ms.cctv && ms.cctv.total) || 0,
                    (ms.idfRooms && ms.idfRooms.total) || 0,
                    (ms.lms && ms.lms.total) || 0,
                    (ms.software && ms.software.total) || 0,
                    (ms.ipPhones && ms.ipPhones.total) || 0,
                    (ms.scrap && ms.scrap.total) || 0,
                ],
                total:
                    ((ms.cctv && ms.cctv.total) || 0) +
                    ((ms.idfRooms && ms.idfRooms.total) || 0) +
                    ((ms.lms && ms.lms.total) || 0) +
                    ((ms.software && ms.software.total) || 0) +
                    ((ms.ipPhones && ms.ipPhones.total) || 0) +
                    ((ms.scrap && ms.scrap.total) || 0),
            };
        },
        laptopsByBrand() {
            return this.buildBreakdownChart(
                this.moduleSummaries.laptops && this.moduleSummaries.laptops.by_brand,
                "Laptops"
            );
        },
        printersByBrand() {
            return this.buildBreakdownChart(
                this.moduleSummaries.printers && this.moduleSummaries.printers.by_brand,
                "Printers"
            );
        },
        cctvByFloor() {
            return this.buildBreakdownChart(
                this.moduleSummaries.cctv && this.moduleSummaries.cctv.by_floor,
                "CCTV"
            );
        },
        scrapByDevice() {
            return this.buildBreakdownChart(
                this.moduleSummaries.scrap && this.moduleSummaries.scrap.by_type,
                "Scrap",
                5
            );
        },
    },
    methods: {
        buildBreakdownChart(rows, moduleKey, topN) {
            var list = Array.isArray(rows) ? rows.slice() : [];
            var labels = [];
            var values = [];
            var moduleKeys = [];
            var total = 0;
            var i;

            list.sort(function (a, b) {
                return (Number(b && b.value) || 0) - (Number(a && a.value) || 0);
            });

            if (topN && list.length > topN) {
                var top = list.slice(0, topN);
                var rest = list.slice(topN);
                var others = 0;
                for (i = 0; i < rest.length; i++) {
                    others += Number(rest[i] && rest[i].value) || 0;
                }
                list = top;
                if (others > 0) {
                    list.push({ label: "Others", value: others });
                }
            }

            for (i = 0; i < list.length; i++) {
                var row = list[i] || {};
                var value = Number(row.value) || 0;
                if (value <= 0) {
                    continue;
                }
                labels.push(row.label || "Unknown");
                values.push(value);
                moduleKeys.push(moduleKey);
                total += value;
            }

            return {
                labels: labels,
                values: values,
                moduleKeys: moduleKeys,
                total: total,
            };
        },
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.push({ path: "/notFound" });
            }
        },
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        onInventoryCategorySelect(payload) {
            if (!payload || !payload.key) {
                return;
            }
            var module = this.modules.find(function (m) {
                return m.key === payload.key;
            });
            if (module) {
                this.openModule(module);
            }
        },
        openModule(module) {
            if (!module || !module.enabled) return;

            var permissionMap = {
                DesktopsAndScreens: "it-desktops-screens-index",
                Laptops: "it-laptops-index",
                WhiteboardsAndProjectors: "it-whiteboards-projectors-index",
                Printers: "it-printers-index",
                Software: "it-software-index",
                IPPhones: "it-ip-phones-index",
                CCTV: "it-cctv-index",
                IDFRooms: "it-idf-rooms-index",
                LMS: "it-lms-index",
                Scrap: "it-scrap-index",
            };
            var requiredPermission = permissionMap[module.key];

            if (requiredPermission && !this.hasPermission(requiredPermission)) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "warning",
                        title: "No access",
                        text: "You do not have permission to view this section.",
                        timer: 2500,
                        showConfirmButton: false,
                    });
                }
                return;
            }
            this.activeModule = module;
        },
        closeModule() {
            this.activeModule = null;
        },
        onModuleChanged(payload) {
            if (payload && payload.refreshYears) {
                this.loadYears(true);
            }
        },
        setDashboardYear(year) {
            if (this.dashboardYear === year) {
                return;
            }
            this.dashboardYear = year;
            this.loadSummary();
        },
        mergeTechnologyYears() {
            var unique = [];
            for (var a = 0; a < arguments.length; a++) {
                var list = arguments[a] || [];
                for (var i = 0; i < list.length; i++) {
                    if (unique.indexOf(list[i]) === -1) {
                        unique.push(list[i]);
                    }
                }
            }
            return unique;
        },
        loadYears(preserveDashboardYear) {
            var self = this;
            if (!this.campusCode) return;

            var previousDashboardYear = this.dashboardYear;
            var showLoader = !preserveDashboardYear;
            if (showLoader) {
                this.yearsLoading = true;
            }
            this.$apollo
                .query({
                    query: YEARS_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var dsPayload =
                        data && data.itDesktopsAndScreensYears
                            ? data.itDesktopsAndScreensYears
                            : null;
                    var lpPayload =
                        data && data.itLaptopsYears
                            ? data.itLaptopsYears
                            : null;
                    var wpPayload =
                        data && data.itWhiteboardsAndProjectorsYears
                            ? data.itWhiteboardsAndProjectorsYears
                            : null;
                    var prPayload =
                        data && data.itPrintersYears
                            ? data.itPrintersYears
                            : null;
                    var swPayload =
                        data && data.itSoftwareYears
                            ? data.itSoftwareYears
                            : null;
                    var ipPayload =
                        data && data.itIpPhonesYears
                            ? data.itIpPhonesYears
                            : null;
                    var cctvPayload =
                        data && data.itCctvCamerasYears
                            ? data.itCctvCamerasYears
                            : null;
                    var idfPayload =
                        data && data.itIdfRoomsYears
                            ? data.itIdfRoomsYears
                            : null;
                    var lmsPayload =
                        data && data.itLmsSystemsYears
                            ? data.itLmsSystemsYears
                            : null;
                    var scrapPayload =
                        data && data.itScrapItemsYears
                            ? data.itScrapItemsYears
                            : null;
                    var dsYears = dsPayload && dsPayload.data ? dsPayload.data : [];
                    var lpYears = lpPayload && lpPayload.data ? lpPayload.data : [];
                    var wpYears = wpPayload && wpPayload.data ? wpPayload.data : [];
                    var prYears = prPayload && prPayload.data ? prPayload.data : [];
                    var swYears = swPayload && swPayload.data ? swPayload.data : [];
                    var ipYears = ipPayload && ipPayload.data ? ipPayload.data : [];
                    var cctvYears = cctvPayload && cctvPayload.data ? cctvPayload.data : [];
                    var idfYears = idfPayload && idfPayload.data ? idfPayload.data : [];
                    var lmsYears = lmsPayload && lmsPayload.data ? lmsPayload.data : [];
                    var scrapYears = scrapPayload && scrapPayload.data ? scrapPayload.data : [];
                    var apiYears = self.mergeTechnologyYears(
                        dsYears, lpYears, wpYears, prYears, swYears, ipYears, cctvYears, idfYears, lmsYears, scrapYears
                    );
                    var lists = buildTechnologyYearLists(apiYears);

                    self.applyYearLists(lists, preserveDashboardYear, previousDashboardYear);
                    self.loadSummary();
                })
                .catch(function () {
                    var lists = buildTechnologyYearLists([]);
                    self.applyYearLists(lists, preserveDashboardYear, previousDashboardYear);
                    self.loadSummary();
                })
                .finally(function () {
                    if (showLoader) {
                        self.yearsLoading = false;
                    }
                });
        },
        applyYearLists(lists, preserveDashboardYear, previousDashboardYear) {
            this.currentYear = lists.currentYear;
            this.yearFilterOptions = lists.filterYears;
            this.formYearOptions = lists.formYears;

            if (preserveDashboardYear) {
                if (previousDashboardYear === TECHNOLOGY_YEAR_FILTER_ALL) {
                    this.dashboardYear = TECHNOLOGY_YEAR_FILTER_ALL;
                } else if (
                    lists.formYears.indexOf(previousDashboardYear) !== -1 ||
                    previousDashboardYear === lists.currentYear
                ) {
                    this.dashboardYear = previousDashboardYear;
                } else {
                    this.dashboardYear = lists.currentYear;
                }
            } else {
                this.dashboardYear = lists.currentYear;
            }
        },
        pluralize(word, count) {
            if (count === 1) return word;
            if (word.endsWith("y") && !/[aeiou]y$/i.test(word)) {
                return word.slice(0, -1) + "ies";
            }
            if (/(s|x|z|ch|sh)$/i.test(word)) {
                return word + "es";
            }
            return word + "s";
        },
        loadSummary() {
            var self = this;
            if (!this.campusCode) return;

            this.summaryLoading = true;
            this.$apollo
                .query({
                    query: SUMMARY_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                        year: this.dashboardYear || null,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var dsPayload = data && data.itDesktopsAndScreensSummary
                        ? data.itDesktopsAndScreensSummary
                        : null;
                    var lpPayload = data && data.itLaptopsSummary
                        ? data.itLaptopsSummary
                        : null;
                    var wpPayload = data && data.itWhiteboardsAndProjectorsSummary
                        ? data.itWhiteboardsAndProjectorsSummary
                        : null;
                    var prPayload = data && data.itPrintersSummary
                        ? data.itPrintersSummary
                        : null;
                    var swPayload = data && data.itSoftwareSummary
                        ? data.itSoftwareSummary
                        : null;
                    var ipPayload = data && data.itIpPhonesSummary
                        ? data.itIpPhonesSummary
                        : null;
                    var cctvPayload = data && data.itCctvCamerasSummary
                        ? data.itCctvCamerasSummary
                        : null;
                    var idfPayload = data && data.itIdfRoomsSummary
                        ? data.itIdfRoomsSummary
                        : null;
                    var lmsPayload = data && data.itLmsSystemsSummary
                        ? data.itLmsSystemsSummary
                        : null;
                    var scrapPayload = data && data.itScrapItemsSummary
                        ? data.itScrapItemsSummary
                        : null;

                    if (dsPayload && !dsPayload.error && dsPayload.data) {
                        self.summary = {
                            total: dsPayload.data.total || 0,
                            desktop_count: dsPayload.data.desktop_count || 0,
                            screen_count: dsPayload.data.screen_count || 0,
                            in_use_count: dsPayload.data.in_use_count || 0,
                            in_stock_count: dsPayload.data.in_stock_count || 0,
                        };
                        self.moduleSummaries.desktops = {
                            total: dsPayload.data.total || 0,
                            desktop_count: dsPayload.data.desktop_count || 0,
                            screen_count: dsPayload.data.screen_count || 0,
                            in_use_count: dsPayload.data.in_use_count || 0,
                            in_stock_count: dsPayload.data.in_stock_count || 0,
                            desktop_in_use_count: dsPayload.data.desktop_in_use_count || 0,
                            desktop_in_stock_count: dsPayload.data.desktop_in_stock_count || 0,
                            screen_in_use_count: dsPayload.data.screen_in_use_count || 0,
                            screen_in_stock_count: dsPayload.data.screen_in_stock_count || 0,
                        };
                        var ds = self.modules.find(function (m) {
                            return m.key === "DesktopsAndScreens";
                        });
                        if (ds) {
                            ds.count = self.summary.total;
                        }
                    } else if (dsPayload && dsPayload.error) {
                        self.toastError(dsPayload.message || "Failed to load summary");
                    }

                    if (lpPayload && !lpPayload.error && lpPayload.data) {
                        self.moduleSummaries.laptops = {
                            total: lpPayload.data.total || 0,
                            in_use_count: lpPayload.data.in_use_count || 0,
                            in_stock_count: lpPayload.data.in_stock_count || 0,
                            by_brand: Array.isArray(lpPayload.data.by_brand)
                                ? lpPayload.data.by_brand
                                : [],
                        };
                        var lp = self.modules.find(function (m) {
                            return m.key === "Laptops";
                        });
                        if (lp) {
                            lp.count = lpPayload.data.total || 0;
                        }
                    }

                    if (wpPayload && !wpPayload.error && wpPayload.data) {
                        self.moduleSummaries.whiteboards = {
                            total: wpPayload.data.total || 0,
                            whiteboard_count: wpPayload.data.whiteboard_count || 0,
                            projector_count: wpPayload.data.projector_count || 0,
                        };
                        var wp = self.modules.find(function (m) {
                            return m.key === "WhiteboardsAndProjectors";
                        });
                        if (wp) {
                            wp.count = wpPayload.data.total || 0;
                        }
                    }

                    if (prPayload && !prPayload.error && prPayload.data) {
                        self.moduleSummaries.printers = {
                            total: prPayload.data.total || 0,
                            in_use_count: prPayload.data.in_use_count || 0,
                            in_stock_count: prPayload.data.in_stock_count || 0,
                            by_brand: Array.isArray(prPayload.data.by_brand)
                                ? prPayload.data.by_brand
                                : [],
                        };
                        var pr = self.modules.find(function (m) {
                            return m.key === "Printers";
                        });
                        if (pr) {
                            pr.count = prPayload.data.total || 0;
                        }
                    }

                    if (swPayload && !swPayload.error && swPayload.data) {
                        self.moduleSummaries.software = {
                            total: swPayload.data.total || 0,
                        };
                        var sw = self.modules.find(function (m) {
                            return m.key === "Software";
                        });
                        if (sw) {
                            sw.count = swPayload.data.total || 0;
                        }
                    }

                    if (ipPayload && !ipPayload.error && ipPayload.data) {
                        self.moduleSummaries.ipPhones = {
                            total: ipPayload.data.total || 0,
                        };
                        var ip = self.modules.find(function (m) {
                            return m.key === "IPPhones";
                        });
                        if (ip) {
                            ip.count = ipPayload.data.total || 0;
                        }
                    }

                    if (cctvPayload && !cctvPayload.error && cctvPayload.data) {
                        self.moduleSummaries.cctv = {
                            total: cctvPayload.data.total || 0,
                            by_floor: Array.isArray(cctvPayload.data.by_floor)
                                ? cctvPayload.data.by_floor
                                : [],
                        };
                        var cctv = self.modules.find(function (m) {
                            return m.key === "CCTV";
                        });
                        if (cctv) {
                            cctv.count = cctvPayload.data.total || 0;
                        }
                    }

                    if (idfPayload && !idfPayload.error && idfPayload.data) {
                        self.moduleSummaries.idfRooms = {
                            total: idfPayload.data.total || 0,
                        };
                        var idf = self.modules.find(function (m) {
                            return m.key === "IDFRooms";
                        });
                        if (idf) {
                            idf.count = idfPayload.data.total || 0;
                        }
                    }

                    if (lmsPayload && !lmsPayload.error && lmsPayload.data) {
                        self.moduleSummaries.lms = {
                            total: lmsPayload.data.total || 0,
                        };
                        var lms = self.modules.find(function (m) {
                            return m.key === "LMS";
                        });
                        if (lms) {
                            lms.count = lmsPayload.data.total || 0;
                        }
                    }

                    if (scrapPayload && !scrapPayload.error && scrapPayload.data) {
                        self.moduleSummaries.scrap = {
                            total: scrapPayload.data.total || 0,
                            by_type: Array.isArray(scrapPayload.data.by_type)
                                ? scrapPayload.data.by_type
                                : [],
                        };
                        var scrap = self.modules.find(function (m) {
                            return m.key === "Scrap";
                        });
                        if (scrap) {
                            scrap.count = scrapPayload.data.total || 0;
                        }
                    }
                })
                .catch(function (err) {
                    var msg =
                        err && err.message
                            ? err.message
                            : "Failed to load technology summary";
                    self.toastError(msg);
                })
                .finally(function () {
                    self.summaryLoading = false;
                });
        },
        toastError(message) {
            if (!this.$swal) return;
            this.$swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: message,
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.tech-hero {
    background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 55%, #eff6ff 100%);
    border-color: #ccfbf1;
}

.tech-hero__eyebrow {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0f766e;
}

.tech-hero__badge {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0.75rem 1rem;
    border-radius: 0.75rem;
    background: rgba(15, 118, 110, 0.08);
    border: 1px solid rgba(15, 118, 110, 0.18);
}

.tech-hero__badge-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #0f766e;
}

.tech-hero__badge-value {
    margin-top: 0.15rem;
    font-size: 0.9rem;
    font-weight: 700;
    color: #134e4a;
}

.tech-year-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem 1rem;
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(15, 118, 110, 0.12);
}

.tech-year-bar__label {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #0f766e;
    white-space: nowrap;
}

.tech-year-bar__icon {
    font-size: 0.8rem;
    opacity: 0.85;
}

.tech-year-bar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.tech-year-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 3.25rem;
    padding: 0.4rem 0.85rem;
    border-radius: 9999px;
    border: 1px solid rgba(15, 118, 110, 0.18);
    background: rgba(255, 255, 255, 0.85);
    color: #115e59;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.2;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.tech-year-pill:hover {
    border-color: #5eead4;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(15, 118, 110, 0.1);
}

.tech-year-pill--active {
    background: #0f766e;
    border-color: #0f766e;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.22);
}

.tech-year-pill--active:hover {
    background: #115e59;
    border-color: #115e59;
    color: #ffffff;
}

.tech-kpi {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    padding: 1.1rem 1.25rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.tech-kpi__label {
    font-size: 0.8rem;
    font-weight: 500;
    color: #6b7280;
}

.tech-kpi__value {
    margin-top: 0.35rem;
    font-size: 1.85rem;
    font-weight: 700;
    color: #111827;
    line-height: 1.1;
}

.tech-kpi__hint {
    margin-top: 0.35rem;
    font-size: 0.7rem;
    font-weight: 500;
    color: #94a3b8;
}

.tech-kpi--ok .tech-kpi__value {
    color: #047857;
}

.tech-kpi--stock .tech-kpi__value {
    color: #1d4ed8;
}

.tech-module {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 7.5rem;
    padding: 1rem 0.75rem;
    border-radius: 1rem;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}

.tech-module--active {
    cursor: pointer;
}

.tech-module--active:hover {
    border-color: #5eead4;
    box-shadow: 0 8px 20px rgba(15, 118, 110, 0.12);
    transform: translateY(-2px);
}

.tech-module--soon {
    opacity: 0.55;
    cursor: not-allowed;
    background: #f9fafb;
}

.tech-module__icon {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
    line-height: 1;
}

.tech-module__label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
}

.tech-module__count {
    margin-top: 0.45rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #0f766e;
    background: #ccfbf1;
    padding: 0.15rem 0.55rem;
    border-radius: 9999px;
}

.tech-module__soon {
    margin-top: 0.45rem;
    font-size: 0.65rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
